import {
  defaultOfferDesign,
  type QuantityOfferConfig,
  type QuantityOfferTier,
} from '@cod/config-schema';
import {
  Alert,
  Box,
  Button,
  Card,
  Icon,
  IconButton,
  Input,
  Pagination,
  Text,
  useToast,
} from '@nimbus-ds/components';
import {
  DiscountCircleIcon,
  DragDotsIcon,
  DuplicateIcon,
  PencilIcon,
  PlusIcon,
  SearchIcon,
  TrashIcon,
} from '@nimbus-ds/icons';
import { Page } from '@nimbus-ds/patterns';
import { useMemo, useRef, useState, type DragEvent } from 'react';
import { useTranslation } from 'react-i18next';
import { ControlledToggle } from '../components/controls.js';
import { OfferListSkeleton } from '../components/skeletons.js';
import { EmptyState } from '../components/states.js';
import { useAppState } from '../state/AppState.js';
import { useNavigationGuard, useUnsavedChanges } from '../state/NavigationGuard.js';
import {
  copyBaseName,
  duplicateInsertIndex,
  offersListView,
  overlappingProductNames,
} from './quantityOffers.model.js';
import { QuantityOfferEditor } from './QuantityOfferEditor.js';

const PAGE_SIZE = 10;

/** Deep-equality for offer lists, used to tell whether staged edits actually differ from the
 *  saved config — so changes that cancel out (toggle off then on, or a reorder put back) clear
 *  the Save/Discard bar instead of leaving it stuck. */
function offersEqual(a: QuantityOfferConfig[], b: QuantityOfferConfig[]): boolean {
  return JSON.stringify(a) === JSON.stringify(b);
}

/**
 * Sales Booster → Quantity Offers list. Offers live in the form config JSONB
 * (`_future.quantity_offers`); their array order IS their priority. List actions
 * (toggle, reorder, delete) are STAGED into `draftOffers` and reach the server only on Save —
 * see `stage` and `reorder`. Add and duplicate both leave for the editor instead — see
 * `duplicate`.
 */
export function QuantityOffers(): JSX.Element {
  const { t } = useTranslation();
  const { config, setConfig, saveQuantityOffers } = useAppState();
  const { addToast } = useToast();
  const guard = useNavigationGuard();
  const [searchInput, setSearchInput] = useState('');
  const [query, setQuery] = useState('');
  const [page, setPage] = useState(1);
  // When set, the editor view replaces the list. `isNew` changes the header copy, and tells the
  // editor the offer is not on the server yet — true for a duplicate as well as an add.
  const [editing, setEditing] = useState<{ id: string; isNew: boolean } | null>(null);
  // Staged edits to the offer list. While non-null, delete (and any reorder/toggle made on top of
  // it) is held here instead of persisted, so a Save/Discard bar can appear.
  // Discard drops back to the saved config; Save persists and clears it.
  const [draftOffers, setDraftOffers] = useState<QuantityOfferConfig[] | null>(null);
  const [saving, setSaving] = useState(false);
  // Drag-to-reorder state, mirroring the Form Designer fields list. The dragged offer id
  // lives in a ref (mutating it on dragstart avoids a re-render that can cancel the native
  // drag); activeId/overId drive the visual feedback.
  const dragId = useRef<string | null>(null);
  const [activeId, setActiveId] = useState<string | null>(null);
  const [overId, setOverId] = useState<string | null>(null);

  // The list works off staged edits when present, else the saved config.
  const workingOffers = useMemo(
    () => draftOffers ?? config?._future.quantity_offers ?? [],
    [draftOffers, config],
  );

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    return workingOffers.filter((o) => o.name.toLowerCase().includes(q));
  }, [workingOffers, query]);

  // Surface the product names targeted by more than one offer so the merchant can fix the
  // overlap before it silently drops the lower-priority offers. Includes disabled offers: this
  // page is where they get edited, so warning early beats warning after the toggle.
  const conflictingProductNames = useMemo(
    () => overlappingProductNames(workingOffers),
    [workingOffers],
  );

  // Registered above the bail-outs below so the hook order never changes. While the editor is
  // open the list's own draft is always null (entering it resolves the staged edits first), so
  // in practice only one of the two ever holds anything — the guard supports both regardless.
  useUnsavedChanges(draftOffers !== null, () => setDraftOffers(null));

  // Defensive: the shell already gates on `status`, so this only fires if the config is
  // momentarily absent. The skeleton is the list's own shape — toolbar, column header, rows.
  if (!config) return <OfferListSkeleton />;

  if (editing) {
    return (
      <QuantityOfferEditor
        offerId={editing.id}
        isNew={editing.isNew}
        onBack={() => setEditing(null)}
      />
    );
  }

  const offers = workingOffers;
  const dirty = draftOffers !== null;
  // "Nothing created yet", "the search matched nothing" and "you deleted them all and have not
  // saved" are three different states — see `offersListView` for why the third one matters.
  const view = offersListView({ offers: offers.length, matches: filtered.length, dirty });
  // Only the first hides the toolbar and becomes a single centred card. That card carries the only
  // Add offer button on screen, so it is a CTA the design checklist allows — the toolbar one it
  // would otherwise duplicate is gone.
  //
  // Without rows there is nothing to search, sort or paginate, so the search box and the column
  // headers go too — but the toolbar ROW stays, because in `removed` it holds the Save/Discard
  // pair that is the only way out of that state.
  const hasRows = offers.length > 0;

  const persist = async (nextOffers: QuantityOfferConfig[]): Promise<boolean> => {
    // Optimistic local update for a snappy list; saveQuantityOffers reconciles from the server
    // response (and merges only the offers slice onto the freshest config).
    setConfig((prev) => ({ ...prev, _future: { ...prev._future, quantity_offers: nextOffers } }));
    // Saving the list empty lands on the onboarding card, which has no search box — so a query
    // left over from before would survive unseen and filter out the next offer they create.
    if (nextOffers.length === 0) {
      setQuery('');
      setSearchInput('');
    }
    const ok = await saveQuantityOffers(nextOffers);
    if (!ok) addToast({ id: 'qo-save', type: 'danger', text: t('common.saveError') });
    return ok;
  };

  // Stage edits as the working draft, unless they cancel back to the saved list — then clear
  // the draft so the Save/Discard bar goes away.
  const stage = (nextOffers: QuantityOfferConfig[]): void => {
    setDraftOffers(offersEqual(nextOffers, config._future.quantity_offers) ? null : nextOffers);
  };

  const saveChanges = async (): Promise<void> => {
    if (draftOffers === null) return;
    setSaving(true);
    const ok = await persist(draftOffers);
    setSaving(false);
    if (ok) setDraftOffers(null);
  };

  const discard = (): void => setDraftOffers(null);

  // Build the duplicate's name as "<base> Copy(n)": strip any existing Copy(k) suffix to get
  // the base, then pick the lowest n that isn't already taken (Copy(1), Copy(2), ...). `among` is
  // passed rather than closed over because `duplicate` runs behind the navigation guard, where the
  // saved config — not the working list — is the set the new name must not collide with.
  const duplicateName = (name: string, among: QuantityOfferConfig[]): string => {
    const copy = t('quantityOffers.copyLabel');
    const base = copyBaseName(name, copy);
    const taken = new Set(among.map((o) => o.name));
    let n = 1;
    while (taken.has(`${base} ${copy}(${n})`)) n += 1;
    return `${base} ${copy}(${n})`;
  };

  // Opening the editor is a section change, and the editor reads the SAVED config — so staged
  // list edits have to be resolved first. This used to be a `disabled` button, which said only
  // that the merchant could not proceed, never why or what to do about it.
  const addOffer = (): void =>
    guard.request(() => {
      const id = crypto.randomUUID();
      // Seed the two tiers shown by default in the editor: a no-discount "buy 1" and a
      // preselected 25%-off "buy 2". The merchant edits or extends them from there.
      const tiers: QuantityOfferTier[] = [
        {
          id: crypto.randomUUID(),
          title: t('quantityOfferEditor.defaultTierOne'),
          quantity: 1,
          preselect: false,
          hide_compare_price: false,
          discount_type: 'none',
          discount_value: 0,
          price_color: '#000000',
          plaque_text: t('quantityOfferEditor.defaultSaveZero'),
          plaque_text_color: '#ffffff',
          plaque_bg_color: '#9aa0a6',
          image_url: '',
        },
        {
          id: crypto.randomUUID(),
          title: t('quantityOfferEditor.defaultTierTwo'),
          quantity: 2,
          preselect: true,
          hide_compare_price: false,
          discount_type: 'percentage',
          discount_value: 25,
          price_color: '#000000',
          plaque_text: 'Save 25%',
          plaque_text_color: '#ffffff',
          plaque_bg_color: '#0074bf',
          image_url: '',
        },
      ];
      const offer: QuantityOfferConfig = {
        id,
        name: t('quantityOffers.newOfferName'),
        enabled: false,
        products: [],
        tiers,
        design: defaultOfferDesign,
      };
      // Seed the offer into the config locally only — no server save yet. The editor persists it
      // on the first save (once it has a name + product) and drops it if the merchant goes back
      // without saving, so a blank offer never reaches the server.
      setConfig((prev) => ({
        ...prev,
        _future: { ...prev._future, quantity_offers: [...prev._future.quantity_offers, offer] },
      }));
      setEditing({ id, isNew: true });
    });

  // Enabling/disabling an offer stages the change too, so it joins the Save/Discard bar
  // (and toggling back to the saved state clears it again).
  const toggle = (id: string): void =>
    stage(offers.map((o) => (o.id === id ? { ...o, enabled: !o.enabled } : o)));

  const resetDrag = (): void => {
    dragId.current = null;
    setActiveId(null);
    setOverId(null);
  };

  // Reorder by id (drop `fromId` at `toId`'s position), so it works regardless of the
  // current page or search filter. The array order IS the offer priority.
  const reorder = (fromId: string, toId: string): void => {
    const from = offers.findIndex((o) => o.id === fromId);
    const to = offers.findIndex((o) => o.id === toId);
    if (from < 0 || to < 0 || from === to) return;
    const next = offers.slice();
    const [moved] = next.splice(from, 1);
    if (moved) next.splice(to, 0, moved);
    // 🚨 Staged like every other list edit — a reorder must NOT write straight through.
    //
    // It used to persist immediately whenever the list was clean, which made dropping a row the
    // one action in the admin that saved without being asked to: no Save/Discard bar appeared, so
    // there was nothing to confirm and nothing to undo, and a drop the merchant made by accident
    // was already on the storefront. Order IS offer priority, so that is a live change to what
    // buyers are shown. It also read as a bug in the opposite direction — a merchant who saw no
    // Save button reasonably concluded the reorder had not been kept.
    stage(next);
  };

  // 🚨 Duplicating OPENS the copy in the editor. It does not just drop a row in the list.
  //
  // A duplicate exists to be changed — the merchant copies an offer to point it at other products
  // or retune its tiers — so parking a row named "X Copy(1)" in the list left them to find it and
  // click the pencil themselves, which is the one thing they were always going to do next.
  //
  // That makes this the same shape as `addOffer`, for the same reason: the editor seeds its draft
  // from the SAVED config, so a copy staged into `draftOffers` would be invisible to it. Seed it
  // into the config locally instead and open it as new.
  //
  // `isNew` is true even though the SOURCE offer is saved, because the COPY is not — backing out
  // without saving must drop it (`dropUnpersisted`), exactly as Discard used to drop the staged
  // row. Nothing reaches the server until the merchant saves, so one icon click still writes
  // nothing.
  const duplicate = (id: string): void =>
    guard.request(() => {
      // The saved config deliberately, not `offers`: the guard may have just discarded staged
      // edits, and the editor could not see them regardless.
      const saved = config._future.quantity_offers;
      const i = saved.findIndex((o) => o.id === id);
      // Only reachable if the source vanished under the guard prompt. Bailing beats opening the
      // editor on an id it cannot resolve, which parks it on a skeleton forever.
      if (i < 0) return;
      const source = saved[i]!;
      const copyId = crypto.randomUUID();
      const next = saved.slice();
      // Below the source AND below the copies it already has, so the series reads original,
      // Copy(1), Copy(2). Splicing at `i + 1` put each new copy above its older siblings, which
      // inverted the ranking — and list order IS offer priority. See `duplicateInsertIndex`.
      next.splice(duplicateInsertIndex(saved, i, t('quantityOffers.copyLabel')), 0, {
        ...source,
        id: copyId,
        name: duplicateName(source.name, saved),
      });
      setConfig((prev) => ({ ...prev, _future: { ...prev._future, quantity_offers: next } }));
      setEditing({ id: copyId, isNew: true });
    });

  const remove = (id: string): void => stage(offers.filter((o) => o.id !== id));

  const editOffer = (id: string): void => guard.request(() => setEditing({ id, isNew: false }));

  const runSearch = (): void => {
    setQuery(searchInput);
    setPage(1);
  };

  const pageCount = Math.max(1, Math.ceil(filtered.length / PAGE_SIZE));
  const currentPage = Math.min(page, pageCount);
  const pageOffers = filtered.slice((currentPage - 1) * PAGE_SIZE, currentPage * PAGE_SIZE);

  return (
    <Page maxWidth="1000px">
      <Page.Body>
        {conflictingProductNames.length > 0 && (
          <Box marginBottom="4">
            <Alert appearance="warning" title={t('quantityOffers.conflictTitle')}>
              <Box display="flex" flexDirection="column" gap="1">
                {conflictingProductNames.map((name) => (
                  <Text key={name}>{t('quantityOffers.conflictWarning', { product: name })}</Text>
                ))}
              </Box>
            </Alert>
          </Box>
        )}

        {view === 'onboarding' ? (
          <Card>
            <Card.Body>
              <Box paddingY="16" paddingX="8">
                <EmptyState
                  icon={
                    <Box
                      display="flex"
                      alignItems="center"
                      justifyContent="center"
                      width="96px"
                      height="96px"
                      marginBottom="4"
                      borderRadius="full"
                      backgroundColor="primary-surface"
                    >
                      <Icon
                        source={<DiscountCircleIcon size="large" />}
                        color="primary-interactive"
                      />
                    </Box>
                  }
                  title={t('quantityOffers.emptyTitle')}
                  text={t('quantityOffers.emptyText')}
                  action={
                    // EmptyMessage stacks its parts with one small gap, which leaves the button
                    // hugging the description. The wrapper pushes it clear of the copy.
                    <Box marginTop="4">
                      <Button appearance="primary" onClick={addOffer}>
                        {t('quantityOffers.addOffer')}
                      </Button>
                    </Box>
                  }
                />
              </Box>
            </Card.Body>
          </Card>
        ) : (
          <>
            {/* Search on the left, Add offer (with Save/Discard) on the right — one aligned row,
                which WRAPS below `md`. Unwrapped, the search box and three buttons shared 358px
                and every one of them was squeezed to unreadable; wrapped, search takes the first
                line and the actions keep their right edge on the second. */}
            <Box
              display="flex"
              alignItems="center"
              justifyContent="space-between"
              gap="2"
              marginBottom="4"
              flexWrap={{ xs: 'wrap', md: 'nowrap' }}
            >
              {/* Search by name. Filtering applies on the Search button or Enter. The wrapper stays
              mounted with no rows so the actions below keep their right-hand edge. */}
              <Box display="flex" gap="2" flex={{ xs: '1 1 100%', md: '1' }} maxWidth="520px">
                {hasRows && (
                  <>
                    <Box flex="1">
                      <Input
                        id="qo-search"
                        placeholder={t('quantityOffers.searchPlaceholder')}
                        value={searchInput}
                        onChange={(e) => setSearchInput((e.target as HTMLInputElement).value)}
                        onKeyDown={(e) => {
                          if (e.key === 'Enter') runSearch();
                        }}
                      />
                    </Box>
                    <Button onClick={runSearch}>
                      <Icon source={<SearchIcon />} color="currentColor" />
                      {t('quantityOffers.search')}
                    </Button>
                  </>
                )}
              </Box>
              <Box display="flex" alignItems="center" gap="2" flex="0 0 auto" marginLeft="auto">
                {/* Save/Discard stay mounted to the left of Add offer and just fade in/out, so Add
                offer never moves. Kept disabled while clean so they're skipped by mouse/keyboard. */}
                <div
                  style={{
                    display: 'flex',
                    gap: 8,
                    opacity: dirty ? 1 : 0,
                    pointerEvents: dirty ? 'auto' : 'none',
                    transition: 'opacity .2s ease',
                    whiteSpace: 'nowrap',
                  }}
                >
                  <Button onClick={discard} disabled={!dirty || saving}>
                    {t('common.discard')}
                  </Button>
                  <Button
                    appearance="primary"
                    onClick={() => void saveChanges()}
                    disabled={!dirty || saving}
                  >
                    {saving ? t('common.saving') : t('common.save')}
                  </Button>
                </div>
                {/* Adding opens the editor (which reads saved config), so it goes through the
                navigation guard — staged changes get resolved by the prompt rather than by a
                button that is simply dead. */}
                {/* One primary action per page (design checklist): Save commits the merchant's work, so
              the Add button is neutral. */}
                <Button onClick={addOffer}>
                  <Icon source={<PlusIcon />} color="currentColor" />
                  {t('quantityOffers.addOffer')}
                </Button>
              </Box>
            </Box>

            {/* Column header row. The priority header deliberately has no drag-handle spacer in front
            of it and absorbs that 24px + gap into its own width instead: the label is wider than
            the 48px number column in es/pt ("Prioridad" / "Prioridade") and used to run into the
            enabled label. Starting it at the row's left edge gives it the room, while the 80px
            total keeps the enabled/name/actions headers over their columns. */}
            {hasRows && (
              <Box
                display={{ xs: 'none', md: 'flex' }}
                alignItems="center"
                gap="2"
                paddingX="4"
                paddingY="2"
              >
                <Box width="80px">
                  <Text fontWeight="medium">{t('quantityOffers.priority')}</Text>
                </Box>
                <Box width="80px">
                  <Text fontWeight="medium">{t('quantityOffers.enabled')}</Text>
                </Box>
                <Box flex="1">
                  <Text fontWeight="medium">{t('quantityOffers.name')}</Text>
                </Box>
                <Box>
                  <Text fontWeight="medium">{t('quantityOffers.actions')}</Text>
                </Box>
              </Box>
            )}

            {view !== 'rows' ? (
              <Card>
                <Card.Body>
                  {/* Both states get the onboarding card's breathing room — an EmptyMessage dropped
                  straight into a Card.Body sits flush against its edges and reads as a stray line
                  of text rather than a state. No CTA in either case: the Add button in the toolbar
                  is the only one. `removed` names the state plainly and points at Save/Discard,
                  because it is the merchant's own staged deletion and the toolbar above is how
                  they resolve it — so it also gets an icon, in the warning palette, to say that
                  something is pending rather than finished. */}
                  <Box paddingY="10" paddingX="8">
                    {view === 'removed' ? (
                      <EmptyState
                        icon={
                          <Box
                            display="flex"
                            alignItems="center"
                            justifyContent="center"
                            width="72px"
                            height="72px"
                            marginBottom="4"
                            borderRadius="full"
                            backgroundColor="warning-surface"
                          >
                            <Icon source={<TrashIcon size="medium" />} color="warning-textHigh" />
                          </Box>
                        }
                        title={t('quantityOffers.removedTitle')}
                        text={t('quantityOffers.removedText')}
                      />
                    ) : (
                      <EmptyState
                        icon={
                          <Box
                            display="flex"
                            alignItems="center"
                            justifyContent="center"
                            width="72px"
                            height="72px"
                            marginBottom="4"
                            borderRadius="full"
                            backgroundColor="neutral-surface"
                          >
                            <Icon source={<SearchIcon size="medium" />} color="neutral-textLow" />
                          </Box>
                        }
                        title={t('quantityOffers.noResults')}
                        text={t('quantityOffers.noResultsText')}
                      />
                    )}
                  </Box>
                </Card.Body>
              </Card>
            ) : (
              <Box display="flex" flexDirection="column" gap="2">
                {pageOffers.map((offer) => {
                  const priority = offers.findIndex((o) => o.id === offer.id) + 1;
                  return (
                    <div
                      key={offer.id}
                      onDragOver={(e: DragEvent) => {
                        e.preventDefault();
                        e.dataTransfer.dropEffect = 'move';
                        if (overId !== offer.id) setOverId(offer.id);
                      }}
                      onDrop={(e: DragEvent) => {
                        e.preventDefault();
                        const from = dragId.current;
                        if (from !== null && from !== offer.id) reorder(from, offer.id);
                        resetDrag();
                      }}
                      style={{
                        opacity: activeId === offer.id ? 0.4 : 1,
                        borderRadius: 8,
                        border: `2px solid ${
                          overId === offer.id && activeId !== null && activeId !== offer.id
                            ? '#4a4a4a'
                            : 'transparent'
                        }`,
                        transition: 'border-color .1s ease',
                      }}
                    >
                      <Card>
                        <Card.Body>
                          {/* 🚨 The name gets its OWN ROW below `md`, above the controls.
                              Nuvemshop's design guidelines say tables are discouraged on mobile —
                              "use the data list component while keeping the same information
                              prioritization" — and that "regardless of the column width, elements
                              should stack". Squeezed into the table's ~100px name column, an
                              ordinary offer name wrapped into a five-line tower beside a toggle,
                              which is what a merchant reported. Stacked, the name reads on one or
                              two lines and the row beneath it holds priority, the toggle and the
                              three actions. */}
                          <Box display={{ xs: 'block', md: 'none' }} marginBottom="2" minWidth="0">
                            <Text fontWeight="medium">{offer.name}</Text>
                          </Box>
                          <Box display="flex" alignItems="center" gap="2">
                            <span
                              draggable
                              onDragStart={(e: DragEvent) => {
                                dragId.current = offer.id;
                                e.dataTransfer.effectAllowed = 'move';
                                // setData makes the drop event fire reliably (esp. Firefox).
                                try {
                                  e.dataTransfer.setData('text/plain', offer.id);
                                } catch {
                                  /* some embedded contexts restrict dataTransfer; drag still works */
                                }
                                // Defer the opacity change: restyling the source node during
                                // dragstart can cancel the native drag in some browsers.
                                requestAnimationFrame(() => setActiveId(offer.id));
                              }}
                              onDragEnd={resetDrag}
                              aria-label={t('quantityOffers.drag')}
                              style={{ cursor: 'grab', display: 'flex', color: '#9aa0a6' }}
                            >
                              <DragDotsIcon />
                            </span>
                            <Box width="48px">
                              <Text>{priority}</Text>
                            </Box>
                            <Box width="80px">
                              <ControlledToggle
                                name={`qo-enabled-${offer.id}`}
                                active={offer.enabled}
                                onChange={() => toggle(offer.id)}
                              />
                            </Box>
                            {/* Desktop keeps the table column; on mobile the name moves to its own
                                row above (see below), because 100px of column turned a normal offer
                                name into a five-line stack. */}
                            <Box flex="1" display={{ xs: 'none', md: 'block' }} minWidth="0">
                              <Text>{offer.name}</Text>
                            </Box>
                            <Box
                              display="flex"
                              gap="1"
                              flex="0 0 auto"
                              marginLeft={{ xs: 'auto', md: 'none' }}
                            >
                              <IconButton
                                size="2rem"
                                title={t('quantityOffers.edit')}
                                source={<PencilIcon />}
                                onClick={() => editOffer(offer.id)}
                              />
                              <IconButton
                                size="2rem"
                                title={t('quantityOffers.duplicate')}
                                source={<DuplicateIcon />}
                                onClick={() => duplicate(offer.id)}
                              />
                              <IconButton
                                size="2rem"
                                title={t('quantityOffers.delete')}
                                source={<TrashIcon />}
                                onClick={() => remove(offer.id)}
                              />
                            </Box>
                          </Box>
                        </Card.Body>
                      </Card>
                    </div>
                  );
                })}
              </Box>
            )}

            {pageCount > 1 && (
              <Box display="flex" justifyContent="center" marginTop="4">
                <Pagination activePage={currentPage} pageCount={pageCount} onPageChange={setPage} />
              </Box>
            )}
          </>
        )}
      </Page.Body>
    </Page>
  );
}
